import { ServerClock, type ErrorCode } from '@pickem/shared';

/**
 * The HTTP client.
 *
 * Two jobs beyond fetching:
 *
 *  1. Every response carries `serverTime`, which is fed into a shared `ServerClock`. All
 *     countdowns and lock displays read from that clock rather than `Date.now()`, so a
 *     phone whose clock is five minutes fast still shows the right thing.
 *  2. Errors become a typed `ApiError` carrying the server's `code`, so screens branch on
 *     `PICK_LOCKED` rather than pattern-matching on message text.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/** Shared across the whole app, including both UI variants. */
export const serverClock = new ServerClock();

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK' | 'UNKNOWN';
  readonly details: unknown;

  constructor(args: {
    status: number;
    code: ApiError['code'];
    message: string;
    details?: unknown;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.details = args.details;
  }

  /** True when retrying could plausibly succeed. */
  get isRetryable(): boolean {
    return this.code === 'NETWORK' || this.status >= 500 || this.status === 429;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }
}

interface Envelope<T> {
  data: T;
  serverTime: string;
}

interface ErrorEnvelope {
  error: { code: ErrorCode; message: string; details?: unknown };
  serverTime: string;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}/api${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      // Sends and receives the session cookie. Without this the API is anonymous.
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    // A failed fetch is the offline case. It must never be reported as a saved pick.
    throw new ApiError({
      status: 0,
      code: 'NETWORK',
      message: 'No connection. Check your network and try again.',
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError({
      status: response.status,
      code: response.ok ? 'UNKNOWN' : 'INTERNAL',
      message: 'The server sent a response we could not read.',
    });
  }

  const serverTime = (payload as { serverTime?: string }).serverTime;
  if (serverTime) serverClock.sync(serverTime);

  if (!response.ok) {
    const body = payload as ErrorEnvelope;
    throw new ApiError({
      status: response.status,
      code: body.error?.code ?? 'UNKNOWN',
      message: body.error?.message ?? 'Something went wrong.',
      details: body.error?.details,
    });
  }

  return (payload as Envelope<T>).data;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', body, query }),
  put: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'PUT', body, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', query }),
};
